import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  BarChart3,
  Brain,
  Building2,
  CheckCircle2,
  HeartHandshake,
  Lightbulb,
  MapPin,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Star,
  Globe,
  Rocket,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const heroStats = [
  { value: "100+", label: "Leading Brands Served" },
  { value: "20+", label: "Years of Combined Experience" },
  { value: "10+", label: "Industry Awards Won" },
];

const storyPillars = [
  { label: "Creative Strategy" },
  { label: "AI-Driven" },
  { label: "Data Analytics" },
  { label: "Team Force" },
];

const beliefs = [
  {
    number: "01",
    icon: Target,
    title: "Perception is Power",
    description:
      "In today's market, how your brand is perceived is just as important as what it delivers. We craft perceptions that convert audiences into advocates.",
  },
  {
    number: "02",
    icon: BarChart3,
    title: "Strategy Before Execution",
    description:
      "Every campaign, every post, every pixel — backed by rigorous strategic thinking and data-driven insights before a single creative decision is made.",
  },
  {
    number: "03",
    icon: HeartHandshake,
    title: "Trust as Currency",
    description:
      "We build brands that earn trust — not just attention. Authentic relationships and honest communication are at the heart of every partnership we forge.",
  },
  {
    number: "04",
    icon: Brain,
    title: "Intelligence Meets Creativity",
    description:
      "Our unique blend of analytical depth and creative brilliance means we don't just think differently — we think smarter, faster, and further ahead.",
  },
  {
    number: "05",
    icon: TrendingUp,
    title: "Scalability by Design",
    description:
      "We don't build for today. Every system, brand identity, and digital ecosystem we create is architectured to scale with your ambition.",
  },
  {
    number: "06",
    icon: Users,
    title: "Team is Everything",
    description:
      "Grawish is not an individual effort — it is a team-driven force where diverse expertise, strategic thinking, and execution excellence converge.",
  },
];

const trackRecord = [
  { value: "100+", label: "Leading Brands in India" },
  { value: "10+", label: "Industry Awards Won" },
  { value: "1", label: "TEDx Speaker on Stage" },
  { value: "4+", label: "Major Publications Featured" },
  { value: "3", label: "IIM Bangalore Certified Leaders" },
  { value: "50+", label: "Award-Winning Campaigns Delivered" },
];

const differentiators = [
  {
    title: "Deeply Personalized",
    description:
      "Every brand we work with gets a custom-built strategy — no templates, no copy-paste solutions. Your brand's DNA guides every decision we make.",
  },
  {
    title: "Measurable Results",
    description:
      "We obsess over outcomes, not outputs. Every campaign we launch is tracked, analyzed, and optimized in real-time for maximum ROI and brand impact.",
  },
  {
    title: "AI + Human Creativity",
    description:
      "We harness the best of artificial intelligence and human creative brilliance — combining data intelligence with emotional storytelling that truly connects.",
  },
  {
    title: "Long-term Partnership",
    description:
      "We don't chase transactions. We forge long-term partnerships built on trust, transparency, and shared ambition for sustained brand success.",
  },
];

const northStar = [
  {
    icon: Target,
    title: "India's #1 Ambition",
    description:
      "Our ambition is clear — to become India's No. 1 digital marketing and personal branding organization, setting the gold standard for what agencies can achieve.",
  },
  {
    icon: Globe,
    title: "Global Footprint",
    description:
      "While India is our home, the world is our stage. We are building a strong, respected global presence that carries the essence of India's creativity and innovation to every corner of the planet.",
  },
  {
    icon: TrendingUp,
    title: "Long-Term Legacy",
    description:
      "We build brands that outlive trends. Grawish focuses on long-term brand value, not fleeting campaigns — because real influence is built on consistency, clarity, and character.",
  },
];

const leaders = [
  {
    name: "Akash Joshi",
    role: "Managing Director",
    image: "/assets/images/Akash-joshi.jpeg",
    quote: "Clarity. Consistency. Long-term Impact.",
    summary:
      "Originally from Indore, Akash Joshi is a strategic thinker and business builder with over two decades of experience across corporate and brand ecosystems. He has worked with 100+ leading brands in India, helping them navigate growth, positioning, and transformation in competitive markets. A national award-winning entrepreneur and a TEDx speaker, Akash has received 10+ industry awards for his contribution to business and brand building. His work has been featured in leading publications such as Mid-Day, Business Standard, The Wise, and YourStory.",
    highlights: [
      "National Award-Winning Entrepreneur",
      "TEDx Speaker",
      "IIM Bangalore – Strategic Digital Marketing & Analytics",
      "20+ Years of Industry Experience",
    ],
  },
  // {
  //   name: "Shalender Singh",
  //   role: "Head of Growth & Partnerships",
  //   image: "/assets/images/shailender.jpeg",
  //   quote: "Growth is not a number — it is a direction.",
  //   summary:
  //     "Shalender Singh is a seasoned growth strategist with deep expertise in scaling brands through performance marketing, strategic alliances, and revenue-driven frameworks. With a background spanning both startup ecosystems and established enterprises, he brings a sharp commercial lens to every engagement and consistently drives measurable business outcomes.",
  //   highlights: [
  //     "Growth & Revenue Strategy Expert",
  //     "Performance Marketing Specialist",
  //     "10+ Years Across Startups & Enterprises",
  //     "Strategic Partnerships & BD Leader",
  //   ],
  // },
  {
    name: "Shikha Joshi",
    role: "Director – Client Success",
    image: "/assets/images/shikha-joshi.png",
    quote: "Breakthrough advertising is rooted in deep insights.",
    summary:
      "Shikha Joshi brings over 13 years of experience in navigating brands through the evolving digital landscape. An engineer by education, she holds B.Tech and M.Tech degrees along with a Digital Marketing certification from IIM Bangalore. Having worked with 50+ brands, she leads strategy and planning, actively mentoring teams and driving meaningful outcomes.",
    highlights: [
      "B.Tech & M.Tech – Engineer Turned Strategist",
      "IIM Bangalore – Digital Marketing Certification",
      "13+ Years of Digital Experience",
      "50+ Brands Transformed",
    ],
  },
  {
    name: "Shreya Juneja",
    role: "Chief Operations Officer",
    image: "/assets/images/shreya-juneja.png",
    quote: "Excellence in execution is where strategy comes alive.",
    summary:
      "Shreya Juneja brings over 6 years of experience across marketing communications and business operations. Having worked both as a brand-side marketer and an agency professional, she has worked with 30+ leading brands including Tata Group, Ashok Leyland, Honeywell, Anand Group, Trident Group, Samasta Foods, VLCC, and has contributed to 10+ award-winning campaigns.",
    highlights: [
      "Operations & P&L Performance Leader",
      "10+ Award-Winning Campaigns",
      "Tata Group, Honeywell, VLCC & 30+ Brands",
      "Culture Builder & Talent Mentor",
    ],
  },
  {
    name: "Dilpreet Singh",
    role: "Head of Creative & Brand Strategy",
    image: "/assets/images/dilpreet-singh.png",
    quote: "Great design does not just look good — it works.",
    summary:
      "Dilpreet Singh leads the creative vision at Grawish, blending design thinking with strategic brand storytelling. With a strong portfolio spanning brand identity, digital campaigns, and content ecosystems, he ensures every visual and narrative touchpoint resonates deeply with the intended audience and leaves a lasting impression.",
    highlights: [
      "Brand Identity & Visual Strategy",
      "Digital Campaign Design Lead",
      "8+ Years in Creative Direction",
      "Multi-Industry Brand Portfolio",
    ],
  },
];

const SectionLabel = ({ children, tone = "purple" }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] ${
      tone === "light"
        ? "border-white/20 bg-white/10 text-white"
        : "border-purple-100 bg-purple-50 text-purple-700"
    }`}
  >
    {children}
  </span>
);

const AboutUsPage = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate("/contact");
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar onContactClick={handleContactClick} alwaysSolid={true} />

      {/* HERO — Dark purple gradient matching PDF */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1a0533] via-[#2d1060] to-[#3d0d6b] pt-24 lg:pt-28 min-h-screen flex items-center">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 h-[600px] w-[600px] rounded-full bg-pink-600/20 blur-[120px]" />
          <div className="absolute bottom-0 left-0 h-[500px] w-[500px] rounded-full bg-purple-800/30 blur-[100px]" />
          {/* Grid overlay like PDF */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 w-full">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-white/90 mb-8">
              <MapPin className="h-3.5 w-3.5 text-pink-400" />
              Headquartered in Indore, India
            </div>

            <h1 className="font-heading text-4xl font-black leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
              Where <em className="not-italic text-purple-300">Art</em>
              <br />
              Meets
              <br />
              <span className="text-pink-400">AI &</span>
              <br />
              <span className="text-purple-300">Ambition</span>
            </h1>

            <p className="mt-8 text-lg text-white/70 max-w-2xl mx-auto sm:text-xl">
              We don't just build brands — we architect influence, engineer
              trust, and craft legacies that outlive trends.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={handleContactClick}
                className="inline-flex items-center gap-2 primary-btn px-8 py-4 rounded-full text-white font-semibold text-sm"
              >
                Book Consultation
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#our-story"
                className="inline-flex items-center gap-2 secondary-btn px-8 py-4 rounded-full font-semibold !text-white text-sm"
              >
                Explore Our Story
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              {heroStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 text-center"
                >
                  <div className="font-heading text-4xl font-black text-pink-400 sm:text-5xl">
                    {stat.value}
                  </div>
                  <div className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/60 sm:text-sm leading-snug">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* OUR STORY — matches PDF layout: image placeholder left, text right */}
      <section id="our-story" className="py-24 bg-[#f5f0ff]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            {/* Left: image placeholder card like PDF */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative rounded-3xl overflow-hidden border border-purple-100 shadow-[0_20px_60px_rgba(123,31,162,0.1)] aspect-[4/3]">
                <img
                  src="/assets/images/story_image.png"
                  alt="Our Story"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-purple-900/10 mix-blend-multiply" />
              </div>

              {/* National Award badge — like PDF */}
              <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-2.5 text-white shadow-lg whitespace-nowrap">
                <Star className="h-4 w-4 fill-white" />
                <span className="text-sm font-semibold">
                  National Award Winning Company
                </span>
              </div>

              {/* Location badge — like PDF */}
              <div className="absolute bottom-6 left-6 flex items-center gap-3 rounded-2xl border border-purple-100 bg-white p-4 shadow-lg">
                <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-base font-bold text-gray-900">
                    Indore, India
                  </div>
                  <div className="text-xs text-gray-500">Our Home City</div>
                </div>
              </div>
            </motion.div>

            {/* Right: story text */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <SectionLabel>Our Story</SectionLabel>
              <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Born from Passion.{" "}
                <span className="gradient-text">Built for Impact.</span>
              </h2>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-gray-600">
                <p>
                  Grawish is born out of a deep passion for art, AI, and the
                  ever-evolving digital landscape. We are not just another
                  agency — we are a movement that redefines what brands can
                  become.
                </p>
                <p>
                  Headquartered in Indore, Grawish is built on the vision of
                  creating meaningful, high-impact brands through a powerful
                  blend of strategy, creativity, and technology-driven
                  execution. With a strong foundation of experience across{" "}
                  <strong className="text-purple-700">
                    100+ leading brands
                  </strong>{" "}
                  in India, we understand what it takes to build not just
                  visibility — but real influence and long-term brand value.
                </p>
                <p>
                  We believe that brands today don't just compete on products —
                  they compete on{" "}
                  <strong className="text-purple-700">
                    perception, positioning, and trust
                  </strong>
                  . Our approach focuses on building strong brand identities,
                  scalable digital ecosystems, and impactful personal brands
                  that stand out.
                </p>
              </div>

              {/* Pill tags matching PDF */}
              <div className="mt-8 flex flex-wrap gap-3">
                {storyPillars.map((p) => (
                  <span
                    key={p.label}
                    className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700"
                  >
                    {p.label}
                  </span>
                ))}
              </div>

              <div className="mt-10">
                <button
                  type="button"
                  onClick={handleContactClick}
                  className="inline-flex items-center gap-2 primary-btn px-8 py-4 rounded-full text-white font-semibold text-sm"
                >
                  Start Your Growth Journey
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE BELIEFS — numbered 01-06 grid matching PDF */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionLabel>What Drives Us</SectionLabel>
            <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Our Core <span className="gradient-text">Beliefs</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
              Every great brand is built on a foundation of unwavering
              principles. These are ours.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {beliefs.map((belief, index) => (
              <motion.div
                key={belief.title}
                className="relative rounded-2xl border border-purple-100 bg-white p-7 shadow-sm hover:shadow-md transition-shadow duration-300"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.07 }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-md">
                    <belief.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-4xl font-black text-gray-100 select-none">
                    {belief.number}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-3">
                  {belief.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {belief.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD — stat grid */}
      <section className="py-24 bg-[#f5f0ff]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionLabel>Our Track Record</SectionLabel>
            <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Numbers That <span className="gradient-text">Tell Our Story</span>
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {trackRecord.map((item, index) => (
              <motion.div
                key={item.label}
                className="rounded-2xl border border-purple-100 bg-white p-8 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <div className="font-heading text-5xl font-black gradient-text sm:text-6xl">
                  {item.value}
                </div>
                <div className="mt-3 text-sm font-semibold text-gray-700 sm:text-base">
                  {item.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY GRAWISH — differentiators */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionLabel>Why Grawish</SectionLabel>
            <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              The Grawish <span className="gradient-text">Difference</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
              We are not just an agency. We are your brand's most committed
              growth partner.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                className="rounded-2xl border border-purple-100 bg-white p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NORTH STAR — dark purple background matching PDF */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-br from-[#1a0533] via-[#2d1060] to-[#3d0d6b]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 h-[400px] w-[400px] rounded-full bg-pink-600/15 blur-[100px]" />
          <div className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-purple-700/20 blur-[80px]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-white/80">
              Our North Star
            </span>
            <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">
              A Vision That{" "}
              <span className="text-pink-400">Rewrites The Rules</span>
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {northStar.map((item, index) => (
              <motion.div
                key={item.title}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-8"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <div className="w-12 h-12 rounded-full bg-white/10 border border-white/15 flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-heading text-xl font-bold text-purple-300 mb-4">
                  {item.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Philosophy quote — from PDF */}
          <div className="mt-16 border-t border-white/10 pt-14 text-center">
            <blockquote className="font-heading text-3xl font-black text-white italic sm:text-4xl lg:text-5xl leading-tight">
              "We don't build campaigns.{" "}
              <span className="text-pink-400">We build empires."</span>
            </blockquote>
            <p className="mt-4 text-xs font-bold uppercase tracking-[0.32em] text-white/40">
              — The Grawish Philosophy
            </p>
          </div>
        </div>
      </section>

      {/* LEADERSHIP — three cards */}
      <section className="py-24 bg-[#f5f0ff]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionLabel>The Visionaries Behind Grawish</SectionLabel>
            <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Meet Our <span className="gradient-text">Leadership</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
              A team-driven force where diverse expertise, strategic thinking,
              and execution excellence come together to deliver measurable
              success.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {leaders.map((leader, index) => (
              <motion.div
                key={leader.name}
                className="rounded-3xl bg-white border border-purple-100 overflow-hidden shadow-sm flex flex-col h-[720px]"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                {/* Photo area — fixed height */}
                <div className="relative h-64 flex-shrink-0 bg-gradient-to-br from-[#f0e8ff] to-[#e0cfff] flex items-end justify-center overflow-hidden">
                  <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-br from-[#3d1270] to-[#5b1a9e]" />
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="relative z-10 w-full h-full object-cover object-top"
                  />
                  {/* Social links */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {/* LinkedIn */}
                    <div className="w-9 h-9 rounded-full bg-[#0A66C2] flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="white"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </div>
                    {/* Instagram */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="white"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Scrollable content area */}
                <div
                  className="flex-1 overflow-y-auto p-7"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  <style>{`.leader-scroll::-webkit-scrollbar { display: none; }`}</style>
                  <div className="text-xs font-bold uppercase tracking-[0.24em] text-purple-600 mb-1">
                    {leader.role}
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900">
                    {leader.name}
                  </h3>

                  <div className="mt-4 border-l-2 border-pink-500 pl-4">
                    <p className="text-sm italic text-purple-900 font-medium">
                      "{leader.quote}"
                    </p>
                  </div>

                  <p className="mt-5 text-sm text-gray-600 leading-relaxed">
                    {leader.summary}
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {leader.highlights.map((highlight) => (
                      <div
                        key={highlight}
                        className="rounded-xl border border-purple-100 bg-purple-50/50 p-3 flex items-start gap-2"
                      >
                        <div className="mt-0.5 w-4 h-4 flex-shrink-0 rounded-full bg-purple-600/10 flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                        </div>
                        <span className="text-xs text-gray-700 leading-relaxed font-medium">
                          {highlight}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — dark purple matching PDF */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-br from-[#1a0533] via-[#2d1060] to-[#3d0d6b]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 h-[400px] w-[400px] rounded-full bg-pink-600/15 blur-[100px]" />
          <div className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-purple-700/20 blur-[80px]" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-white/80 mb-8">
              Ready to Level Up?
            </div>
            <h2 className="font-heading text-5xl font-black text-white leading-tight sm:text-6xl lg:text-7xl">
              Let's Build Something
              <br />
              Extraordinary
              <br />
              <span className="text-pink-400">Together</span>
            </h2>
            <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
              Whether you're a startup finding your voice or an established
              brand seeking evolution — Grawish is your partner in building
              influence that lasts.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white/70">
                <MapPin className="h-4 w-4" /> Indore, Madhya Pradesh, India
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white/70">
                <Globe className="h-4 w-4" /> Serving India & Global Clients
              </span>
            </div>

            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-purple-900 shadow-lg hover:bg-purple-50 transition-colors sm:text-base"
              >
                Start a Conversation
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-sm font-bold text-white hover:bg-white/10 transition-colors sm:text-base"
              >
                <Users className="h-4 w-4" />
                Meet the Team
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer onContactClick={handleContactClick} />
    </div>
  );
};

export default AboutUsPage;
